import { isRecord } from "@atoms/atom-types";
import type { OperationTypeNode } from "graphql";
import { isMarkedConnectionFailure } from "./connectionFailure";

/**
 * The backend answers GraphQL errors with HTTP 200, so a 5xx status on the
 * GraphQL endpoint comes from a proxy in front of it:
 * - 503: the proxy had no backend to forward to; the request never ran.
 * - 502: the backend dropped the connection; a mutation may have run.
 * - 504: the proxy gave up after a long wait; retrying only adds to it.
 */
const STATUS_TRANSIENT_FOR_EVERY_OPERATION = 503;
const STATUS_TRANSIENT_FOR_QUERIES = 502;

function readNetworkError(error: unknown): unknown {
  // An ApolloError (Apollo Client 3) wraps the transport error and carries
  // the GraphQL errors of the response next to it.
  if (isRecord(error) && "networkError" in error) {
    const { graphQLErrors } = error;
    if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
      return undefined;
    }
    return error.networkError ?? undefined;
  }
  return error;
}

/**
 * Whether a failed GraphQL request is worth repeating because it most likely
 * failed on the way to the backend. For a read (query, subscription): a proxy
 * 503 or 502, a fetch that got no response (`TypeError: Failed to fetch`) or
 * a WebSocket connection failure marked by `markConnectionFailure`. For a
 * mutation only a proxy 503, because after anything else it may have run.
 *
 * A failure without a response counts only when fetch itself failed. Any
 * other client-side error (an unparsable response, a protocol or
 * serialization error, an aborted request, an exception of another link),
 * any other status and GraphQL errors are not transient.
 *
 * Accepts the raw error a link sees and an `ApolloError` wrapping it.
 */
export function isTransientTransportError(
  error: unknown,
  operationType: OperationTypeNode | "query" | "mutation" | "subscription",
): boolean {
  const readOnly = operationType !== "mutation";
  const networkError = readNetworkError(error);
  if (
    isMarkedConnectionFailure(error) ||
    isMarkedConnectionFailure(networkError)
  ) {
    return readOnly;
  }
  if (!isRecord(networkError)) {
    return false;
  }
  const { statusCode } = networkError;
  if (typeof statusCode !== "number") {
    // fetch rejects with a TypeError when no response arrived. Any other
    // error without a status (a GraphQL error, an aborted request, a parse
    // failure) would fail the same way again.
    return readOnly && networkError instanceof TypeError;
  }
  if (statusCode === STATUS_TRANSIENT_FOR_EVERY_OPERATION) {
    return true;
  }
  return statusCode === STATUS_TRANSIENT_FOR_QUERIES && readOnly;
}
